import { createContext, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ConfigProvider } from 'antd';
import arEG from 'antd/locale/ar_EG';
import enUS from 'antd/locale/en_US';
import trTR from 'antd/locale/tr_TR';
import dayjs from 'dayjs';
import 'dayjs/locale/ar';
import 'dayjs/locale/en';
import 'dayjs/locale/tr';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const { i18n } = useTranslation();
  const [direction, setDirection] = useState('ltr');
  const [locale, setLocale] = useState(enUS);

  useEffect(() => {
    const currentLang = i18n.language || localStorage.getItem('i18nextLng') || 'en';
    const isRTL = currentLang.startsWith('ar');
    setDirection(isRTL ? 'rtl' : 'ltr');
    setLocale(isRTL ? arEG : currentLang.startsWith('tr') ? trTR : enUS);

    // Update dayjs locale
    dayjs.locale(currentLang);

    // Update document direction and lang attribute
    document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLang;

    // Update body class for RTL/LTR styling
    document.body.classList.remove('rtl', 'ltr');
    document.body.classList.add(isRTL ? 'rtl' : 'ltr');

    // Update page title
    document.title = 'STUDY MORE';
  }, [i18n.language]);

  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang).then(() => {
      const isRTL = lang === 'ar';
      setDirection(isRTL ? 'rtl' : 'ltr');
      setLocale(isRTL ? arEG : lang === 'tr' ? trTR : enUS);

      // Update dayjs locale
      dayjs.locale(lang);

      document.documentElement.dir = isRTL ? 'rtl' : 'ltr';
      document.documentElement.lang = lang;

      document.body.classList.remove('rtl', 'ltr');
      document.body.classList.add(isRTL ? 'rtl' : 'ltr');

      // Update page title
      document.title = 'STUDY MORE';
    });
  };

  const value = {
    language: i18n.language,
    direction,
    changeLanguage,
    isRTL: direction === 'rtl',
  };

  return (
    <LanguageContext.Provider value={value}>
      <ConfigProvider locale={locale} direction={direction}>
        {children}
      </ConfigProvider>
    </LanguageContext.Provider>
  );
};

